/* Mobile drill-in panel for the mega menu.
   The mega submenu (wrapper--mega) slides in as a panel when its parent link
   gets `submenu-opened` (handled by the existing MegaMenuSection in base.js).
   This script closes the panel from the in-panel "Back" button and resets it
   whenever the nav drawer is closed, so it always reopens on the top-level list.

   Below 1200px, mobile-mega-grid.css holds every panel open instead, so the
   drill-in never runs there. This file therefore also owns two mobile-only
   concerns that a stylesheet cannot express:

     1. FOCUS. mega-menu-brand-link.liquid hardcodes `tabindex="-1"` on every
        level-2/3 link and nothing puts it back, because the `submenu-opened`
        handshake that normally would never fires.
     2. CHROME OFFSET. The drawer and the theme's dim overlay both need to
        start below the site chrome, but that chrome is an announcement-bar
        section PLUS the header section, and only the header's own height is
        exposed as a CSS token (`--navbar-height`). Measuring the header
        section's rect is the only way to get the real bottom edge.

   The layering itself is NOT done here. An earlier version of this file tried
   to lift the chrome above the overlay from JavaScript, by walking the DOM and
   rewriting position/z-index on whatever it found. That was guesswork standing
   in for facts, and it did not work. The theme's own critical.css turned out to
   say plainly that `.page-header` already carries `z-index: 16` against the
   overlay's `z-index: 15`, and only loses because the theme forces
   `position: static` on it while the drawer is open. mobile-mega-grid.css
   section 10 now undoes exactly that, in CSS, and all of the DOM-walking here
   is deleted.

   What remains is self-contained: it reads DOM state and touches nothing in
   base.js or page-header.js. page-header.js only calls setTabindex under
   `isHeaderWithDesktopNav && isDesktop` inside enableStickyHeader, so it cannot
   run below 1200px and the two cannot fight over the same attribute. */
(function () {
  "use strict";

  var OPEN_CLASS = "submenu-opened";
  var MENU_OPEN_CLASS = "menu-open";
  var OVERLAY_CLASS = "menu-drawer-overlay-on";
  var DESKTOP_QUERY = "(min-width: 1200px)";
  var DRAWER_SELECTOR = ".wt-drawer--nav";
  var DEEP_LINKS = "[data-menu-level='2'],[data-menu-level='3']";
  var OFFSET_PROP = "--mmg-header-offset";

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  /* The theme toggles both `menu-open` and `menu-drawer-overlay-on` on <body>
     when the nav drawer opens (confirmed in critical.css, which keys the dim
     overlay and the header's `position: static` off the latter). Either one is
     treated as open; <html> is checked too, since nothing in this theme's files
     pins which element carries the class. */
  function isMenuOpen() {
    var b = document.body.classList;
    var h = document.documentElement.classList;
    return (
      b.contains(MENU_OPEN_CLASS) ||
      b.contains(OVERLAY_CLASS) ||
      h.contains(MENU_OPEN_CLASS) ||
      h.contains(OVERLAY_CLASS)
    );
  }

  function setTabindexOn(nodes, value) {
    Array.prototype.forEach.call(nodes, function (a) {
      a.setAttribute("tabindex", value);
    });
  }

  /* Mobile only: level-2/3 links are reachable exactly while the drawer is
     open. Above 1200px this is a no-op, leaving the desktop behaviour (and
     page-header.js's sticky-header handling) untouched. */
  function syncDrawerTabindex() {
    if (isDesktop()) return;

    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;

    setTabindexOn(drawer.querySelectorAll(DEEP_LINKS), isMenuOpen() ? "0" : "-1");
  }

  /* Bottom edge of the site chrome, in viewport coordinates.

     `<header class="... page-header">` is a direct child of <body>, and the
     announcement bar is the `<section class="... wt-announcement">` directly
     above it. Because getBoundingClientRect() is viewport-relative, the
     header's own bottom edge already includes the announcement bar's height,
     so one measurement covers both without either being named here. Clamped at
     0 so a header scrolled out of view offsets nothing rather than a negative
     amount. */
  function chromeBottom() {
    var header =
      document.querySelector("header.page-header") ||
      document.querySelector(".page-header") ||
      document.querySelector("#header");
    if (!header) return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }

  /* Written on <html> rather than on the drawer, because the elements that
     consume it -- the drawer, `.menu-drawer-overlay` and `.page-overlay` --
     are siblings, so the value has to be inherited from a common ancestor.
     :root carries a CSS fallback for the case where this never runs. */
  function syncChromeOffset() {
    var root = document.documentElement;

    if (isDesktop() || !isMenuOpen()) {
      root.style.removeProperty(OFFSET_PROP);
      return;
    }

    root.style.setProperty(OFFSET_PROP, chromeBottom() + "px");
  }

  function sync() {
    syncDrawerTabindex();
    syncChromeOffset();
  }

  function closePanel(parentLink) {
    if (!parentLink) return;
    parentLink.classList.remove(OPEN_CLASS);
    var wrapper = parentLink.nextElementSibling;
    if (wrapper) {
      setTabindexOn(wrapper.querySelectorAll(DEEP_LINKS), "-1");
    }
  }

  function closeAllPanels(root) {
    (root || document)
      .querySelectorAll(".wt-page-nav-mega__link--parent." + OPEN_CLASS)
      .forEach(closePanel);
  }

  document.addEventListener("click", function (e) {
    // In-panel back button -> close just that panel
    var back = e.target.closest("[data-mega-back]");
    if (back) {
      e.preventDefault();
      var item = back.closest(".wt-page-nav-mega__item--parent");
      if (item) closePanel(item.querySelector(".wt-page-nav-mega__link--parent"));
      return;
    }

    // Closing/toggling the drawer (its close button, the menu trigger, or the
    // dark backdrop) resets any open panel so the menu reopens on the list.
    if (
      e.target.closest(".wt-drawer__close") ||
      e.target.closest('[rel="toggle-menu"]') ||
      e.target.classList.contains("wt-drawer--nav")
    ) {
      // Desktop only. Below 1200px the panels are meant to stay open, and
      // closePanel would re-apply tabindex="-1" to every brand link.
      if (isDesktop()) closeAllPanels();
    }
  });

  // The drawer opens and closes by toggling classes on <body>, so that is the
  // signal to resync. Attribute-only, no subtree walk.
  var classObserver = new MutationObserver(sync);
  var observerOptions = { attributes: true, attributeFilter: ["class"] };
  if (document.body) classObserver.observe(document.body, observerOptions);
  classObserver.observe(document.documentElement, observerOptions);

  var resizeTimer = null;
  window.addEventListener("resize", function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(sync, 150);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", sync);
  } else {
    sync();
  }
})();
